import { IconButton, TextareaAutosize, TextareaAutosizeProps, TextField, TextFieldProps, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { useCallback } from "react";
import { CustomFormLabel } from "./CustomFormLabel";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";

export const CustomTextField = styled((props: TextFieldProps) => (
  <TextField {...props} />
))(({ theme }) => ({
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#ffffff",
    borderRadius: "6px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    "& fieldset": {
      borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.2)" : "#b0bec5",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "#607d8b",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1976d2",
      borderWidth: "1.5px",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "9px 13px",
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: theme.palette.mode === "dark" ? "#e6e5e8" : "#1a202c",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    fontWeight: 500,
    color: "#4a5568",
    marginTop: "-0.4rem",
  },
  "& .MuiInputLabel-shrink": {
    fontSize: "0.85rem",
    fontWeight: 600,
    marginTop: "0.1rem",
  },
  "& .MuiOutlinedInput-input::-webkit-input-placeholder": {
    color: "#4a5568",
    opacity: 0.88,
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: "#4a5568",
    opacity: 0.88,
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#b0bec5",
    borderRadius: "6px",
  },
  "& .MuiSvgIcon-root": {
    color: "#4a5568",
  },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    backgroundColor: theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#f8f9fb",
    fontWeight: "600",
  },
  "& .MuiOutlinedInput-input.Mui-disabled::-webkit-input-placeholder": {
    color: "#4a5568",
    opacity: 0.88,
  },
}));

export const CustomShrinkTextField = styled((props: TextFieldProps) => (
  <TextField {...props} />
))(({ theme }) => ({
  "& input[type=number]": {
    "-moz-appearance": "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    "-webkit-appearance": "none",
    margin: 0,
  },
  '& .MuiInputLabel-shrink': {
    fontSize: '1.3rem',
    fontWeight: 600, // Increase the font size here

  },


  '& .MuiInputLabel-shrink + .MuiOutlinedInput-root': {

    '& fieldset': {
      legend: {

        maxWidth: '100%', // Ensure it does not overflow
        fontSize: '1.05rem',

      },
    },
  },

}));


interface CustomNumberFieldProps extends Omit<TextFieldProps, "value"> {
  readOnlyMode?: boolean;
  value?: string | number;
}

export const CustomNumberField: React.FC<CustomNumberFieldProps> = (props) => {
  const { readOnlyMode = false, value, onChange, ...rest } = props;

  // Process Initial Value
  const processedValue = useCallback(() => {
    if (value === undefined || value === null || value === "") return "";
    const valStr = value.toString();

    if (valStr.startsWith("0.") || valStr === "0") {
      return valStr; // Keep "0." and "0"
    }

    return valStr.replace(/^0+/, ""); // Remove leading zeros from integers
  }, [value])();

  // ReadOnly Mode: Display text instead of input
  if (readOnlyMode) {
    return (
      <Typography variant="body1" padding={1} fontSize="0.8rem">
        {value || "--"}
      </Typography>
    );
  }

  return (
    <CustomTextField
      {...rest}
      value={processedValue} // Use processed value correctly
      onChange={(evt) => {
        if (!props.onChange) return;
        if (props.value?.toString().includes(".") && evt.target.value === ".")
          return;
        let newEvt = { ...evt };
        const onlyNums = newEvt.target.value
          .replace(/[^0-9.]/g, "")
          .replace(/(\..*)\./g, "$1");
        newEvt.target.value = onlyNums;
        props.onChange(newEvt);
      }}
      InputProps={{ inputProps: { min: 0 } }} // Prevents negative values in UI, allow zero
    />
  );
};


export const ExpandableCustomTextFieldStyled = styled(
  (props: TextareaAutosizeProps) => <TextareaAutosize {...props} />,
)(({ theme }) => ({
  // Styles for native textarea (TextareaAutosize) instead of MUI TextField
  padding: " 5px",
  fontSize: "0.875rem",
  fontFamily: "Rubik, sans-serif",
  color: "rgb(38, 38, 38)",
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#dee3e9"
    }`,
  borderRadius: "5px",
  outline: "none",
  resize: "vertical",
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#fff",
  "&::placeholder": {
    fontFamily: "inherit !important",
    color: "#767e89",
    opacity: 1,
  },
  "&:disabled": {
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#f8f9fb",
    color: "#767e89",
  },
}));


export const ExpandableTextField: React.FC<TextareaAutosizeProps> = (props) => {
  const { minRows, ...rest } = props;
  return (
    <ExpandableCustomTextFieldStyled
      {...rest}
      minRows={minRows}
      style={{ width: "100%" }}
    />
  );
};

interface CustomPasswordFieldProps extends Omit<TextFieldProps, "value"> {
  readOnlyMode?: boolean;
  value?: string | number;
  heading?: string;
  showPassword?: boolean;
  onShowPasswordChange?: (show: boolean) => void;
}

export const CustomPasswordField: React.FC<CustomPasswordFieldProps> = (
  props
) => {
  const {
    value,
    onChange,
    heading,
    showPassword: externalShowPassword,
    onShowPasswordChange,
    ...rest
  } = props;
  const [internalShowPassword, setInternalShowPassword] = React.useState(false);

  // Use external showPassword if provided, otherwise use internal state
  const showPassword =
    externalShowPassword !== undefined
      ? externalShowPassword
      : internalShowPassword;

  const handleClickShowPassword = () => {
    const newShowPassword = !showPassword;
    if (onShowPasswordChange) {
      onShowPasswordChange(newShowPassword);
    } else {
      setInternalShowPassword(newShowPassword);
    }
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  return (
    <>
      {heading && <CustomFormLabel>{heading}</CustomFormLabel>}
      <CustomTextField
        {...rest}
        value={value}
        onChange={onChange}
        type={showPassword ? "text" : "password"}
        InputProps={{
          ...rest.InputProps,
          endAdornment: (
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
              sx={{
                color: showPassword ? "primary.main" : "grey.500",
                "&:hover": {
                  color: showPassword ? "primary.dark" : "grey.700",
                },
              }}
            >
              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          ),
        }}
      />
    </>
  );
};

